/**
 * Pure helpers for the agent's voice (speech-to-speech) mode.
 *
 * Kept out of `useAgentController` so the parts that are easy to get wrong —
 * the internal-message filter guarding a PII leak, the mic error mapping and
 * the transcript hand-off — are testable without a browser or a live session.
 */

/** Message prefixes the client sends to the agent but must never render as chat. */
const INTERNAL_PREFIXES = [
  "[Form Submitted:",
  "[Terminwunsch:",
  "[Termin ausgewählt:",
  "[File Attachment]",
  "[Seite nicht gefunden:",
  "Aktueller Seiten-Kontext",
] as const;

/**
 * True for protocol text the client sends on the user's behalf.
 *
 * In voice mode user turns are rendered from the server's transcript echo, and
 * that echo includes these. `submitFormToAgent` deliberately renders no local
 * bubble for `[Form Submitted: lead_contact_form] {"email": ...}` — without this
 * guard the echo would print the visitor's own contact data into the chat.
 */
export const isInternalMessage = (text: string): boolean =>
  INTERNAL_PREFIXES.some((prefix) => text.trimStart().startsWith(prefix));

/** Normalizes user text so a typed message matches its server echo. */
export const normalizeUserText = (text: string): string =>
  text.trim().toLowerCase().replace(/\s+/g, " ");

export type MicErrorKind =
  "denied" | "noDevice" | "deviceBusy" | "insecure" | "unknown";

/**
 * Maps a `getUserMedia` / session-start failure to the copy key that explains it.
 * Keyed on `DOMException.name`, which is stable across browsers (the messages
 * are not).
 */
export const classifyMicError = (error: unknown): MicErrorKind => {
  const name =
    error instanceof DOMException || error instanceof Error
      ? error.name
      : undefined;

  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      // Browsers do not distinguish "denied" from "prompt dismissed".
      return "denied";
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
      return "noDevice";
    case "NotReadableError":
    case "TrackStartError":
      // Another app (Teams, Zoom, …) holds the device.
      return "deviceBusy";
    case "SecurityError":
      return "insecure";
    default:
      return "unknown";
  }
};

/** `true` when the browser cannot do voice at all (insecure origin, sandbox). */
export const isVoiceUnsupported = (): boolean =>
  typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia;

export interface TranscriptEntry {
  role: "user" | "assistant";
  content: string;
}

const MAX_TRANSCRIPT_TURNS = 15;
const MAX_TRANSCRIPT_CHARS = 4000;

/**
 * Renders the local chat history as a contextual update for a restarted session.
 *
 * Switching between text and voice means `endSession()` + `startSession()`, and
 * the ElevenLabs-side history does not survive that. `dynamic_variables` can't
 * carry it (init-only, and would need a dashboard prompt placeholder), so it goes
 * in as a contextual update, which needs no agent-side configuration.
 *
 * Capped because `sendContextualUpdate` is not meant for kilobytes and the agent
 * prompt has a hard limit — oldest turns are dropped first.
 */
export const buildTranscriptUpdate = (
  history: TranscriptEntry[]
): string | null => {
  const turns = history
    .filter(
      (entry) => entry.content.trim() && !isInternalMessage(entry.content)
    )
    .slice(-MAX_TRANSCRIPT_TURNS)
    .map(
      (entry) =>
        `${entry.role === "user" ? "Nutzer" : "Du"}: ${entry.content.trim()}`
    );

  if (turns.length === 0) return null;

  // Drop from the front until the rendered block fits.
  while (turns.length > 1 && turns.join("\n").length > MAX_TRANSCRIPT_CHARS) {
    turns.shift();
  }

  return `Bisheriger Chat-Verlauf, führe das Gespräch fort:\n${turns.join("\n")}`;
};

/**
 * Contextual update for the session that takes over when the input modality
 * changes.
 *
 * The mode is fixed at connection init, so a switch is `endSession()` plus a
 * fresh `startSession()` — a *new* ElevenLabs conversation with no memory of the
 * old one. Everything it needs to continue seamlessly goes in here: where the
 * visitor stands right now, which conversation this one continues (the id is the
 * only link between the two halves, in the dashboard as much as for the agent)
 * and what was said so far.
 */
export const buildHandoffUpdate = ({
  pageContext,
  previousConversationId,
  history,
}: {
  pageContext: string;
  previousConversationId: string | null;
  history: TranscriptEntry[];
}): string =>
  [
    pageContext,
    previousConversationId
      ? `Fortsetzung der Conversation ${previousConversationId} — ` +
        `derselbe Nutzer, nur eine andere Eingabe-Modalität (Text ↔ Sprache).`
      : null,
    buildTranscriptUpdate(history),
  ]
    .filter((part): part is string => Boolean(part))
    .join("\n");
